import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CuratedListsService } from '../../services/curatedListsService';
import { getCuratedListCover } from '../../utils/curatedListCover';
import styles from './CuratedListRail.module.css';

const LIST_TYPE_ORDER = [
    'starter_pack',
    'essentials',
    'before_you_watch',
    'weekend_picks',
    'director_spotlight',
    'genre_guide',
    'era_guide',
    'staff_picks',
    'other',
];

export const LIST_TYPE_LABELS = {
    starter_pack:       'Starter Packs',
    essentials:         'Essentials',
    before_you_watch:   'Before You Watch',
    weekend_picks:      'Weekend Picks',
    director_spotlight: 'Director Spotlights',
    genre_guide:        'Genre Guides',
    era_guide:          'Era Guides',
    staff_picks:        'Staff Picks',
    other:              'More Picks',
};

function CuratedListCard({ list }) {
    const cover = getCuratedListCover(list);
    const progressText = list.progress
        ? `${list.progress.verified} of ${list.progress.total} verified`
        : null;

    return (
        <Link to={`/discover/lists/${list.slug}`} className={styles.card}>
            <div className={styles.coverWrap}>
                {cover.url ? (
                    <img src={cover.url} alt={cover.alt} className={styles.cover} loading="lazy" />
                ) : (
                    <div className={styles.coverFallback}>Aluvo</div>
                )}
            </div>
            <div className={styles.copy}>
                {list.listType && <span className={styles.type}>{list.listType.replaceAll('_', ' ')}</span>}
                <h3>{list.title}</h3>
                <p>{list.description}</p>
                {progressText && (
                    <div className={styles.progress}>
                        <span>{progressText}</span>
                        <div className={styles.progressTrack}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${list.progress.total ? (list.progress.verified / list.progress.total) * 100 : 0}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Link>
    );
}

export default function CuratedListRail({
    category,
    featured = true,
    title = 'Aluvo Picks',
    showHeader = true,
    showTypeFilters = false,
    emptyMessage = null,
}) {
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeType, setActiveType] = useState('all');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        CuratedListsService.list({ featured, category, limit: 50 })
            .then((data) => {
                if (!cancelled) setLists(data.curatedLists || []);
            })
            .catch(() => {
                if (!cancelled) setLists([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [category, featured]);

    const availableTypes = useMemo(() => {
        const types = new Set(lists.map((list) => list.listType).filter(Boolean));
        return LIST_TYPE_ORDER
            .filter((type) => types.has(type))
            .map((type) => ({ value: type, label: LIST_TYPE_LABELS[type] ?? type.replaceAll('_', ' ') }));
    }, [lists]);

    useEffect(() => {
        if (activeType !== 'all' && !availableTypes.some((type) => type.value === activeType)) {
            setActiveType('all');
        }
    }, [activeType, availableTypes]);

    const visibleLists = activeType === 'all'
        ? lists
        : lists.filter((list) => list.listType === activeType);

    if (!lists.length) {
        if (loading || !emptyMessage) return null;
        return (
            <section className={styles.section}>
                {showHeader && (
                    <div className={styles.header}>
                        <h2>{title}</h2>
                        <span>Editorial watch paths</span>
                    </div>
                )}
                <div className={styles.empty}>{emptyMessage}</div>
            </section>
        );
    }

    return (
        <section className={styles.section} aria-labelledby={showHeader ? 'curated-lists-title' : undefined}>
            {showHeader && (
                <div className={styles.header}>
                    <h2 id="curated-lists-title">{title}</h2>
                    <span>Editorial watch paths</span>
                </div>
            )}
            {showTypeFilters && availableTypes.length > 1 && (
                <div className={styles.typeFilters} aria-label="Filter Aluvo Picks by type">
                    <button
                        type="button"
                        className={`${styles.typeFilter} ${activeType === 'all' ? styles.typeFilterActive : ''}`}
                        onClick={() => setActiveType('all')}
                    >
                        All
                    </button>
                    {availableTypes.map((type) => (
                        <button
                            key={type.value}
                            type="button"
                            className={`${styles.typeFilter} ${activeType === type.value ? styles.typeFilterActive : ''}`}
                            onClick={() => setActiveType(type.value)}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>
            )}
            <div className={styles.rail}>
                {visibleLists.map((list) => <CuratedListCard key={list.id} list={list} />)}
            </div>
        </section>
    );
}
